<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="true"
                :showAddNew="true"
                :btnTitle="'Add Investigation Head'"
                title="Investigation Head"
                @onClickAddNewButton="openLocationAddModal"
                @onClickCloseButton="goToTree"
            />
        </div>

        <div class="row p-2">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label" for="select2-basic">Category</label>
                <v-select
                    v-model="investigationType"
                    label="name"
                    :options="investigationHeadTypes"
                    :reduce="name => name.id"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label" for="select2-basic">Group</label>
                <v-select
                    v-model="headGroup"
                    label="name"
                    :options="investigationHeadGroups"
                    :reduce="name => name.id"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label" for="select2-basic">Head</label>
                <AsyncSelect
                    placeholder="Select Head"
                    v-model="headId"
                    label="name"
                    :api-service="fetchInvestigationHeadEndpointDropdown"
                    :format-label="option => option.name"
                    ref="productField"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label">Service Name</label>
                <AsyncSelect
                    placeholder="Select Service Name"
                    v-model="serviceId"
                    :api-service="fetchProductList"
                    :reduce="name => name.id"
                    :format-label="option => option.text"
                    :additional-query="{product_type: 'services'}"
                    label="text"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label class="align-middle">Status</label>
              <v-select
                  placeholder="Select Status"
                  v-model="status"
                  :options="statusListData"
                  :reduce="name => name.value"
                  label="name"
              />
            </div>
        </div>

        <div class="d-flex justify-content-end">
            <div class="col-12 col-sm-6 col-md-4 col-lg-1 mt-auto">
                <button @click="getInvestigationHead" class="btn btn-primary waves-effect waves-float waves-light">
                    Go
                </button>
            </div>
        </div>

        <div class="col-12 px-2 my-2">
            <div class="card card-height">
                <div class="table-responsive">
                    <table class="table">
                        <thead>
                        <tr>
                            <th>Type</th>
                            <th>Group</th>
                            <th>Head</th>
                            <th>Service Name</th>
                            <th>Investigation Report Name</th>
                            <th>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, i) in tableItems" :key="i">
                            <td>{{ formatName(item.investigation_type) }}</td>
                            <td>{{ item.group_name }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.product_name }}</td>
                            <td>{{ formatName(item.investigation_head) }}</td>
                            <td>
                                <img
                                    @click="openEditModal(item)"
                                    class="cursor-pointer img-width"
                                    src="/app-assets/images/icons/pencil-alt-solid.svg"
                                    alt="Edit"
                                />
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    <p v-if="!tableItems.length" class="text-center mt-5">No data</p>
                </div>
            </div>
        </div>

        <Loader v-if="loading"/>

        <div class="px-2 position-absolute bottom-0">
            <Pagination
                :offset="10"
                @onPageChange="onPageChange"
                ref="pagination"
            />
        </div>

        <InvestigationAddModal
          :showParent="true"
          modalTitle="Add Investigation Head"
          ref="locationAdd"
          :type="editInvestigationHeadTypes"
          :sampleTypes="sampleTypes"
          :testMethods="testMethods"
          :reports="reportList"
          :product="product"
          :formData="locationAddData"
          :status="statusList"
          :group="investigationHeadGroups"
          :isFromList=true
          @onSubmitLocation="handleInvestigationAdd"
        />

        <InvestigationAddModal
          submitEvent="onUpdateLocation"
          :showParent="true"
          modalTitle="Edit Investigation Head"
          ref="locationedit"
          :type="editInvestigationHeadTypes"
          :sampleTypes="sampleTypes"
          :testMethods="testMethods"
          :reports="reportList"
          :product="product"
          :machineDataNoteMaster="machineDataNoteMaster"
          :formData="locationEditData"
          :status="statusList"
          :group="investigationHeadGroups"
          @onUpdateLocation="handleInvestigationUpdate"
        />
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, inject } from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import Pagination from "@/components/atom/Pagination.vue";
import handleHospital from '@/services/modules/hospital';
import handlePurchase from '@/services/modules/purchase'
import objToArray from '@/services/utils/object-to-array';
import InvestigationAddModal from '@/components/molecule/company/hospital/investigation-head/InvestigationAddModal.vue';
import Loader from '@/components/atom/LoaderComponent'
import { useRoute, useRouter } from 'vue-router';
import { useStore } from "vuex";
import handleInventory from '@/services/modules/inventory'

const $route = useRoute();
const $router = useRouter();
const $store = useStore();

const investigationHeadTypes = ref([]);
const editInvestigationHeadTypes = ref([]);
const investigationHeadGroups = ref([]);
const investigationType = ref(null);
const headGroup = ref(null);
const headId = ref(null);
const serviceId = ref(null);
const tableItems = ref([]);
const page = computed(() => $route.query.page);
const offset = ref(20);
const loading = ref(false);
const status = ref('');

const locationEditData = ref({});
const product = ref(null);
const machineDataNoteMaster = ref(null);
const reportList = ref([]);
const sampleTypes = ref([]);
const testMethods = ref([]);
const statusList = ref([]);
const statusListData = ref([]);
const locationedit = ref(null);
const locationAdd = ref(null);

const locationAddData = ref({
  name         : '',
  investigation_head : null,
  code         : '',
  investigation_type: null,
  has_discharge_certificate: false,
  status       : 'active',
  unit         : '',
  reference_value : '',
  sample       : null,
  test_method  : null,
  company_id   : '',
  head_group_id: null,
  product_id   : null,
  short_description : '',
  full_description  : '',
  default_result: '',
  machine_type  : '',
  machine_key_note_master_id  : '',
  note_master_type  : '',
  is_result_dropdown  : false,
  decimal_count: ''
});

const showError = inject('showError');
const showSuccess = inject('showSuccess');

const {
  fetchInvestigationHeadTypes,
  fetchInvestigationHeadGroup,
  fetchInvestigationHeadGetAll,
  fetchInvestigationHeadEndpointDropdown,
  fetchNoteMasterList,
  updateInvestigationHead,
  storeInvestigationHead
} = handleHospital();
const { fetchProductList } = handlePurchase();

const {
  fetchHome,
} = handleInventory()

watch(investigationType, (newVal) => {
  if (newVal) {
    getInvestigationGroup();
  }
});

const getInvestigationGroup = () => {
  return fetchInvestigationHeadGroup($route.params.companyId, investigationType.value).then((res) => {
    if (res.status) {
      investigationHeadGroups.value = res.data;
    }
  });
};

const formatName = (value) => {
  if (!value) return '';
  return value
    .replace(/_/g, ' ')
    .replace(/^./, c => c.toUpperCase());
};

const getQuery = () => {
  let query = '';
  query += `?company_id=${$route.params.companyId}`;
  query += '&offset=' + offset.value;
  if (page.value) query += '&page=' + page.value;
  if (investigationType.value) query += `&investigation_type=${investigationType.value}`;
  if (status.value) query += `&status=${status.value}`;
  if (headGroup.value) query += `&head_group=${headGroup.value}`;
  if (headId.value) query += `&head_id=${headId.value.id}`;
  if(serviceId.value) query += `&service_id=${serviceId.value}`;
  return query;
};

const getInvestigationHead = async (resetPage = true) => {
  if (resetPage) {
    let routeQuery = Object.assign({}, $route.query);
    routeQuery.page = 1;
    await $router.push({ path: $route.path, query: routeQuery });
  }
  loading.value = true;
  let query = getQuery();

  fetchInvestigationHeadGetAll(query).then((res) => {
    tableItems.value = [];
    if (res.status) {
      tableItems.value = res.data.data;
      setPagination(res.data);
      loading.value = false;
    }
  });
};

const setPaginationData = (data) => {
  $store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  });
};

const openEditModal = (item) => {
  locationEditData.value = {
    id: item.id,
    name: item.name,
    code: item.code,
    decimal_count: item.decimal_count,
    investigation_type: item.investigation_type,
    status: item.status,
    company_id: $route.params.companyId,
    head_group_id: item.head_group_id,
    investigation_head: item.investigation_head,
    has_discharge_certificate: item.has_discharge_certificate || false,
    unit: item.unit,
    reference_value: item.reference_value,
    sample: item.sample,
    test_method: item.test_method,
    product_id: item.product_id,
    short_description: item.short_description,
    full_description: item.full_description,
    default_result: item.default_result,
    machine_type: item.machine_type,
    machine_key_note_master_id: item.machine_key_note_master_id,
    note_master_type: item.note_master_type,
    is_result_dropdown: item.is_result_dropdown === 1 ? true : false,
    investigation_report_type: item.investigation_report_type,
  };
  product.value = {
    id: item.product_id,
    name: item.product_name
  };
  machineDataNoteMaster.value = {
    id: item.machine_key_note_master_id,
    name: item.machine_note_master_name
  };
  locationedit.value.toggleModal();
};

const handleInvestigationUpdate = async () => {
  const modal = locationedit.value;
  modal.loading = true;
  try {
    const res = await updateInvestigationHead(locationEditData.value);
    if (!res.status) {
      showError(res.message);
      return;
    }
    modal.toggleModal();
    await getInvestigationHead();
    showSuccess(res.message);
  } catch (err) {
    showError('Failed to update investigation head');
  } finally {
    modal.loading = false;
  }
};

const handleInvestigationAdd = async () => {
    try {
      locationAddData.value.company_id = $route.params.companyId

      const res = await storeInvestigationHead(locationAddData.value)

      if (!res.status) {
        showError(res.message)
        return
      }

      showSuccess(res.message);
      await getInvestigationHead();
      locationAdd.value.toggleModal();
    } catch (err) {
      console.log('Something went wrong')
    }
}

onMounted(async () => {
  const companyId = $route.params.companyId;
  const typeQuery = `?company_id=${companyId}`;
  const noteQuery = `?company_id=${companyId}&note_type=investigation_report_name`;
  statusListData.value = []

  try {
    const typeRes = await fetchInvestigationHeadTypes(typeQuery);
    if (typeRes.status) {
      editInvestigationHeadTypes.value = typeRes.data.investigationHeadTypes;
      investigationHeadTypes.value = objToArray(typeRes.data.investigationHeadTypes);
      sampleTypes.value = typeRes.data.sampleTypes;
      testMethods.value = typeRes.data.testMethods;
      statusList.value = typeRes.data.status;
    } 

    await getInvestigationGroup();
    await getInvestigationHead(false);

    const homeRes = await fetchHome()
    if (homeRes.data && homeRes.data.status){
      const allowedStatuses = ['active', 'inactive'];

      statusListData.value = Object.keys(homeRes.data.status)
          .filter(key => allowedStatuses.includes(key))
          .map(key => ({
            value: key,
            name: homeRes.data.status[key]
          }));
    }

    const noteRes = await fetchNoteMasterList(noteQuery);
    if (noteRes.status) reportList.value = noteRes.data.data;
  } catch (e) {
    showError('Failed to load initial data');
  }
});

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, $route.query);
  routeQuery.page = page;
  await $router.push({ path: $route.path, query: routeQuery });
  await getInvestigationHead(false);
};

const goToTree = () => {
  $router.back()
};

const openLocationAddModal = () => {
  locationAddData.investigation_type = investigationType
  locationAdd.value.toggleModal();
}
</script>

<style scoped>
.img-width {
  width: 13px;
  cursor: pointer;
}
</style>
